import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, Save, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { productService } from '../../services/productService';
import { Product, Category, Brand, Location } from '../../types';

function ProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reference: '',
    codeArticle: '',
    designation: '',
    categoryId: '',
    brandId: '',
    unit: 'piece' as Product['unit'],
    barcode: '',
    prixUnitaireHT: 0,
    conditionnement: '',
    seuilMin: 10,
    description: '',
    image: '',
    defaultLocationId: ''
  });

  const isEditing = !!id;

  useEffect(() => {
    loadFormData();
  }, [id]);

  const loadFormData = async () => {
    try {
      const [categoriesData, brandsData, locationsData] = await Promise.all([
        productService.getCategories(),
        productService.getBrands(),
        productService.getLocations()
      ]);
      
      setCategories(categoriesData);
      setBrands(brandsData);
      setLocations(locationsData);
      
      if (id) {
        const productData = await productService.getProduct(id);
        if (productData) {
          setProduct(productData);
          setFormData({
            reference: productData.reference,
            codeArticle: productData.codeArticle,
            designation: productData.designation,
            categoryId: productData.categoryId,
            brandId: productData.brandId,
            unit: productData.unit,
            barcode: productData.barcode || '',
            prixUnitaireHT: productData.prixUnitaireHT,
            conditionnement: productData.conditionnement,
            seuilMin: productData.seuilMin,
            description: productData.description || '',
            image: productData.image || '',
            defaultLocationId: productData.defaultLocationId
          });
        }
      }
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les données du formulaire',
        type: 'error'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      
      if (isEditing && product) {
        await productService.updateProduct(product.id, {
          ...formData,
          isActive: product.isActive
        });
        
        addNotification({
          userId: user.id,
          title: 'Produit modifié',
          message: `${formData.designation} a été modifié avec succès`,
          type: 'success'
        });
      } else {
        await productService.createProduct({
          ...formData,
          isActive: true
        });
        
        addNotification({
          userId: user.id,
          title: 'Produit créé',
          message: `${formData.designation} a été créé avec succès`,
          type: 'success'
        });
      }
      
      navigate('/products');
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: error instanceof Error ? error.message : 'Impossible de sauvegarder le produit',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const unitOptions = [
    { value: 'piece', label: 'Pièce(s)' },
    { value: 'm', label: 'Mètre(s)' },
    { value: 'm2', label: 'Mètre(s) carré(s)' },
    { value: 'kg', label: 'Kilogramme(s)' },
    { value: 'l', label: 'Litre(s)' },
    { value: 'ml', label: 'Millilitre(s)' },
    { value: 'cm', label: 'Centimètre(s)' },
    { value: 'mm', label: 'Millimètre(s)' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/products')}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="p-2 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? 'Modifier le produit' : 'Nouveau produit'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-8 sm:px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations de base */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de base</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Référence *
                  </label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    placeholder="VIS-001"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code article *
                  </label>
                  <input
                    type="text"
                    value={formData.codeArticle}
                    onChange={(e) => setFormData({ ...formData, codeArticle: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    placeholder="VIS4X50"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Désignation *
                  </label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    placeholder="Vis à bois 4x50mm"
                  />
                </div>
              </div>
            </div>

            {/* Classification */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Classification</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie *
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marque *
                  </label>
                  <select
                    value={formData.brandId}
                    onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Sélectionner une marque</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unité *
                  </label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value as Product['unit'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    {unitOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Informations commerciales */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations commerciales</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix unitaire HT (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.prixUnitaireHT}
                    onChange={(e) => setFormData({ ...formData, prixUnitaireHT: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conditionnement *
                  </label>
                  <input
                    type="text"
                    value={formData.conditionnement}
                    onChange={(e) => setFormData({ ...formData, conditionnement: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    placeholder="Boîte de 100 pièces"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code-barres
                  </label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value.replace(/\D/g, '') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="1234567890123"
                    maxLength={13}
                  />
                </div>
              </div>
            </div>

            {/* Stock et emplacement */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Stock et emplacement</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seuil minimum *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.seuilMin}
                    onChange={(e) => setFormData({ ...formData, seuilMin: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                    placeholder="10"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emplacement par défaut *
                  </label>
                  <select
                    value={formData.defaultLocationId}
                    onChange={(e) => setFormData({ ...formData, defaultLocationId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="">Sélectionner un emplacement</option>
                    {locations.filter(l => l.isActive).map(location => (
                      <option key={location.id} value={location.id}>
                        {location.fullCode} - {location.commentaire}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Description et image */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations complémentaires</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Description détaillée du produit..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL de l'image
                  </label>
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/products')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProductForm;